import { FilterContext, FilterProvider, FilterProviderRef, FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import GroupByPeriodFilter from "components/filters/Filters/GroupByPeriodFilter";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import SingleBookkeepingFilter from "components/filters/Filters/SingleBookkeepingFilter";
import { mapGroupByPeriodFilterToInstruction, mapResponseToSingleBookkeepingFilter, mapResponseToGroupByPeriodFilter } from "components/filters/mappingUtils";
import { NoDataAvailableWarning } from "components/NoDataAvailableDiv";
import { Loading, Panel } from "components/panels";
import Table from "components/Tables/Table";
import React, { useContext, useState } from "react";
import { useLoaderData } from "react-router";
import { canReadSales } from "utilities/documentUtils";
import { getMoneyFormatter } from "utilities/formatter";
import { useCustomQuery } from "utilities/hookutils";
import { InvoiceChart } from "./SalesChart";

const mapFilterValuesToInstruction = (values: FilterValues): server.dto.ListDocumentTotalInstruction => ({
  bookkeepingId: values.bookkeepings.selectedBookkeepings.length > 0 ? values.bookkeepings.selectedBookkeepings[0].bookkeepingId : undefined,
  period: mapGroupByPeriodFilterToInstruction(values.groupByPeriod),
});

const mapResponseToFilterValues = (response: server.dto.ListDocumentTotalFilters): Partial<FilterValues> => ({
  bookkeepings: mapResponseToSingleBookkeepingFilter(response.bookkeeping),
  groupByPeriod: mapResponseToGroupByPeriodFilter(response.period)
});

export const InvoiceTotalsApp = () => {
  const viewModel = useLoaderData<server.viewmodel.InvoiceTotalFrontModel>();
  const filterRef = React.useRef<FilterProviderRef>(null);

  const [instruction, onInstructionChange] = useState<server.dto.ListDocumentTotalInstruction>({
    ...viewModel.initialInstruction,
    cache: "invoice_Totals"
  });
  const [initialLoading, setInitialLoading] = useState(true);

  const api = useCustomQuery({
    queryFn: async () => {
      return window.CoreApi.Invoice.GetDocumentTotals(getCacheFriendlyInstruction(instruction));
    },
    enabled: !!instruction.bookkeepingId,
    queryKey: ["documentTotals", "invoice", instruction],
    onSuccess: (response) => {
      if (initialLoading) {
        filterRef.current.setDefaultValues(() => {
          return mapResponseToFilterValues(viewModel.defaultFilters);
        });
      }
      filterRef.current.updateValues(() => {
        return mapResponseToFilterValues(response.data.request);
      });
      setInitialLoading(false);
    },
    cacheTime: 0
  });

  const onFilterChange = (filter: FilterValues) => {
    const newInstruction = mapFilterValuesToInstruction(filter);
    onInstructionChange({
      ...newInstruction,
      cache: "invoice_Totals"
    });
  };

  return <FilterProvider
    ref={filterRef}
    onFilterChange={onFilterChange}
  >
    <div className="flex-column panel-gap">
      <FilterHeader.Filters>
        <div className="selectiontool-header-filters">
          <SingleBookkeepingFilter initialBookkeeping={viewModel.defaultFilters.bookkeeping} />
          <GroupByPeriodFilter />
        </div>
      </FilterHeader.Filters>

      <TableAndChart
        items={api.data?.data.data ?? []}
        isLoading={api.isLoading}
      />
    </div>
  </FilterProvider>
}

interface TableAndChartProps {
  items: server.dto.GroupedInvoiceTotals[]
  isLoading: boolean
}

const TableAndChart = (props: TableAndChartProps) => {
  const filterContext = useContext(FilterContext);
  const moneyFormatter = getMoneyFormatter();

  const selectedBookkeepings = filterContext.getValue("bookkeepings").selectedBookkeepings;
  const selectedBookkeeping = selectedBookkeepings.length > 0 ? selectedBookkeepings[0] : null;
  const currency = selectedBookkeeping ? selectedBookkeeping.baseCurrency : "";

  if (!selectedBookkeeping) {
    return <NoDataAvailableWarning>
      {RESX.Bookkeeping.SelectABookkeeping}
    </NoDataAvailableWarning>;
  }

  if (props.isLoading) {
    return <Loading />;
  }

  const displaySalesExcludingTax = canReadSales("invoice");
  const displaySalesIncludingTax = canReadSales("invoice");
  const displayTotalSettled = canReadSales("invoice");
  const displayChart = canReadSales("invoice");

  const totalCount = props.items.reduce((acc, item) => acc + item.count, 0);
  const totalSalesExcludingTax = props.items.reduce((acc, item) => acc + item.totalSalesExcludingTax, 0);
  const totalSalesIncludingTax = props.items.reduce((acc, item) => acc + item.totalSalesIncludingTax, 0);
  const totalSettled = props.items.reduce((acc, item) => acc + item.totalSettled, 0);

  const getFooterEntries = () => {
    const entries = [
      null,
      <div key="count">{totalCount} {RESX.Invoice.Invoices}</div>,
      <div key="totalSalesExcludingTax" className="s1_cell_financialamount">{moneyFormatter.format(totalSalesExcludingTax)} {currency.toUpperCase()}</div>,
      <div key="totalSalesIncludingTax" className="s1_cell_financialamount">{moneyFormatter.format(totalSalesIncludingTax)} {currency.toUpperCase()}</div>,
      <div key="totalOutstanding" className="s1_cell_financialamount">{moneyFormatter.format(totalSalesIncludingTax - totalSettled)} {currency.toUpperCase()}</div>
    ];

    return entries;
  }

  return <>
    <Panel
      panelClassName="s1_panel"
      title={selectedBookkeeping.name}
    >
      <Table
        items={props.items}
        isLoading={props.isLoading}
        columns={[{
          key: "period",
          header: RESX.DateTime.Period,
          render: item => item.label,
          size: "minmax(auto, 1fr)",
        }, {
          key: "count",
          header: RESX.Document.NumberOfDocuments,
          render: item => <>{item.count} {RESX.Invoice.Invoices}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalSalesExcludingTax",
          header: RESX.Financial.TotalSalesExTax,
          className: "s1_cell_financialamount",
          hidden: !displaySalesExcludingTax,
          render: item => <>{moneyFormatter.format(item.totalSalesExcludingTax)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalSalesIncludingTax",
          header: RESX.Financial.TotalSalesIncTax,
          className: "s1_cell_financialamount",
          hidden: !displaySalesIncludingTax,
          render: item => <>{moneyFormatter.format(item.totalSalesIncludingTax)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalOutstanding",
          header: RESX.Financial.TotalOutstanding,
          className: "s1_cell_financialamount",
          hidden: !displayTotalSettled,
          render: item => <>{moneyFormatter.format(item.totalSalesIncludingTax - item.totalSettled)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }]}
        footers={[{
          key: "total",
          className: "panel-header-bg",
          entries: getFooterEntries(),
        }]}
        getKey={i => i.label}
      />
    </Panel>

    {displayChart && <Panel
      panelClassName="s1_panel"
      title={RESX.Financial.SalesTotal}
    >
      <InvoiceChart
        period={filterContext.getValue("groupByPeriod").groupBy}
        invoiceTotals={props.items}
        currency={currency}
      />
    </Panel>}
  </>
}